/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React, { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { di } from 'react-magnetic-di';
import { defaults as ReactSweetStateDefaults } from 'react-sweet-state';

import { FlagsProvider } from '@atlaskit/flag';
import { SmartCardProvider } from '@atlaskit/link-provider/context';
import { fg } from '@atlaskit/platform-feature-flags';
import { useInteractionContext } from '@atlaskit/react-ufo/interaction-context';
import { useRovoAgentActionAnalytics } from '@atlaskit/rovo-agent-analytics/actions';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { AgentOverriderProvider } from '@atlassian/conversation-assistant-agent/controllers/agent-overrider';
import { useChatModeState } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeState';
import { useChatModeStoreActions as useChatModeStoreActionsRefresh } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStoreActions';
import { useCurrentPageAsContext } from '@atlassian/conversation-assistant-chat-prompt-input/useCurrentPageAsContext';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import {
	RovoEntitlementSetter,
	useRovoEntitlement,
	useRovoEntitlementActions,
} from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { AnalyticsContextWithSourceAndProperties } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import {
	useAIMateExperienceTracker,
	useUFOLabelStackSetter,
} from '@atlassian/conversation-assistant-instrumentation/main';
import {
	setupSentryClient,
	type SentrySetupFailure,
} from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { ExperienceStart } from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import {
	AssistanceServiceImpl,
	AssistanceServiceProvider,
	useAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useChatContextProvider } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useChatSignalsActions } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import { useChatMessagesActions } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';
import { useReconnectStoreActions } from '@atlassian/conversation-assistant-store/controllers/reconnect';
import {
	useStagingAreaActions,
	useStagingAreaState,
} from '@atlassian/conversation-assistant-store/controllers/staging-area';
import {
	StoreUserMessagesMigrationRunner,
	useStoreUserMessagesPreference,
} from '@atlassian/conversation-assistant-store/controllers/store-user-messages-be';
import {
	useUIStore,
	useUIStoreActions,
	useUIStoreSubscription,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useTasksStoreActions as useUnifiedTasksStoreActions } from '@atlassian/conversation-assistant-store/controllers/unified-tasks';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { useHighlightActionsEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useHighlightActionsEditorPlaceholder';
import { RovoSettingsModalSurface } from '@atlassian/rovo-settings-modal/common/utils/rovo-settings-config';
import { RovoSettingsModalProvider } from '@atlassian/rovo-settings-modal/ui/rovo-settings-modal-provider';
import { useSpaceStoreActions } from '@atlassian/rovo-spaces/controllers/space-store';
import { AifcDebugger } from '@atlassian/smart-creation/aifc-debugger';

import { AI_MATE_EXPERIENCE_ID } from './common/constants/private';
import { useRovoChatCapabilities } from './common/hooks/useRovoChatCapabilities';
import { BackBehaviorOverrideProvider } from './common/ui/BackBehaviorOverrideProvider';
import { ErrorBoundary as AIMateErrorBoundary } from './common/ui/error-boundary';
import { StoreErrorFlagProvider } from './common/ui/hooks/use-show-store-error-flag/StoreErrorFlagProvider';
import { useShowStoreErrorFlag } from './common/ui/hooks/use-show-store-error-flag/useShowStoreErrorFlag';
import { createGlobalConfig } from './common/utils/global-config/createGlobalConfig';
import { GlobalConfigProvider } from './common/utils/global-config/GlobalConfigProvider';
import { type GlobalConfig } from './common/utils/global-config/types';
import { useGlobalConfig } from './common/utils/global-config/useGlobalConfig';
import { setRovoChatProduct } from './common/utils/product-config';
import { SideNavProvider } from './common/utils/side-nav-context/SideNavProvider';
import { UIConfigProvider } from './common/utils/ui-config/UIConfigProvider';
import { useUIConfig } from './common/utils/ui-config/useUIConfig';
import { useAssistanceServiceParams } from './common/utils/use-assistance-service-params';
import { useActiveAction } from './controllers/active-action';
import { AgentVersioningConfigProvider } from './controllers/agent-versioning-config/AgentVersioningConfigProvider';
import { AnalyticsPropertiesUpdater } from './controllers/analytics';
import { useDraftDataAction } from './controllers/bridge-api/draft-data-action';
import { useEnvironmentAction } from './controllers/bridge-api/environment-action';
import { BridgeAPIHandlers } from './controllers/bridge-api/handlers';
import { useMessageStreamAction } from './controllers/bridge-api/message-stream-action/useMessageStreamAction';
import { useChatInitiatorStoreActions } from './controllers/chat-initiator-store/useChatInitiatorStoreActions';
import { useChatMessageResearchActions } from './controllers/chat-message-research-store/useChatMessageResearchActions';
import { useOnNewConversationStartedWithSendMessage } from './controllers/chat-mode-store';
import { ChatStoreContainer } from './controllers/chat-store/ChatStoreContainer';
import { createViewDeadAirBroker } from './controllers/chat-store/createViewDeadAirBroker';
import { useChatStore } from './controllers/chat-store/useChatStore';
import { useChatStoreActions } from './controllers/chat-store/useChatStoreActions';
import { WarmChatContextBannerLinkController } from './controllers/warm-chat-context-banner-link/WarmChatContextBannerLinkController';
import { WarmChatFetches } from './controllers/warm-chat-fetches';
import { ConversationAssistantRender } from './ConversationAssistantRender';
import { getRovoChatXpcContextValues } from './getRovoChatXpcContextValues';
import { useConversationAssistantProviderValues } from './provider-value-stabilization';
import { RovoChatXpcContextOverride } from './RovoChatXpcContextOverride';
import { shouldUseCurrentNonEditingPageAsContext } from './shouldUseCurrentNonEditingPageAsContext';
import {
	type ConversationAssistantConfig,
	type ConversationAssistantProps,
	ConversationAssistantUIModes,
} from './types';
import { RovoChatUFOStatePublisher } from './ui/ufo-state-publisher';
import { FirstChatOpenUFODataPublisher } from './ui/ufo-state-publisher/FirstChatOpenUFODataPublisher';
import { ViewDeadAirProvider } from './ui/view-dead-air-tracker/ViewDeadAirProvider';
import { getPackageInfo } from './utils/package-info';

/**
 * WARNING: This wrapper component initializes the Chat Store and can be rendered multiple times
 * within one application. DO NOT add any rendering logic (modals, overlays, UI components) here
 * as it will cause multiple instances to be rendered. Keep this component pure and only use it
 * for state initialization and context provision.
 *
 * If you need to add UI elements, place them in the appropriate render components instead.
 */
const ConversationAssistantWrapper = ({
	onPromptSent,
	children,
}: {
	onPromptSent?: ConversationAssistantConfig['onPromptSent'];
	children: React.ReactNode;
}) => {
	di(useAIMateExperienceTracker, useGlobalConfig);
	const showFlag = useShowStoreErrorFlag();
	const {
		getContentBody,
		getStagingSpace,
		processMessageStreamAction,
		processMessageStreamComplete,
		processMessageStreamError,
		processMessageStreamActionClassification,
		broadcastConfirmedActions,
		broadcastStreamStatus,
	} = useMessageStreamAction();
	const { requestBrowserContent } = useEnvironmentAction();
	const { requestJiraContext } = useDraftDataAction();
	const experienceTracker = useAIMateExperienceTracker();
	const assistanceService = useAssistanceService();
	const { getIsMounted: getIsChatMounted } = useUIStoreActions();
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();
	const onSentrySetupFailure = useCallback(
		(failure: SentrySetupFailure) => {
			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'sentryClientSetup',
				attributes: failure,
			});
		},
		[sendAnalyticsEvent],
	);
	const { browserContextSanitizer, enableBrowserContextBanner, elements } = useUIConfig();
	const { productHeader, siteId } = useGlobalConfig();
	const { currentContext } = useCurrentPageAsContext();
	const [{ currentConversationId }] = useChatStore();
	// Read the active conversation's "use current page as context" toggle from the
	// chat mode store so the chat-context provider can derive useCurrentBrowsingContext.
	const { useCurrentPageContext } = useChatModeState(currentConversationId);
	const chatContextStoreActions = useChatContextProvider({
		requestBrowserContent,
		requestJiraContext,
		getContentBody,
		getStagingSpace,
		useCurrentNonEditingPageAsContext: shouldUseCurrentNonEditingPageAsContext({
			enableBrowserContextBanner,
			shouldShowContextBanner: elements?.shouldShowContextBanner,
			pageType: currentContext?.type,
		}),
		useCurrentPageContext,
	});
	const { setStatus, setConversationError } = useChatSignalsActions();
	const { getMessages, setMessages } = useChatMessagesActions();
	const { setIsRovoEnabled } = useRovoEntitlementActions();
	const { startReconnection, endReconnection, isReconnecting } = useReconnectStoreActions();

	useEffect(() => {
		setupSentryClient({
			packageVersion: getPackageInfo().version,
			product: productHeader,
			onSetupFailure: onSentrySetupFailure,
		});
	}, [productHeader, onSentrySetupFailure]);

	const {
		processStreamResearch,
		updateResearchMessageId,
		markIncompleteResearchesErrored,
		completeAllSteps,
		clearMessageResearch,
		addImplicitResearchPlan,
		resetHasReceivedAnswerPart,
	} = useChatMessageResearchActions();
	const { getChatInitiator, initChatInitiator } = useChatInitiatorStoreActions();
	const { insertHighlightActionsEditorPlaceholder } = useHighlightActionsEditorPlaceholder();
	const {
		setMessageIdSelectedForPreview,
		setStagingAreaMode,
		getMessageIdSelectedForPreview,
		getInvocationIdSelectedForPreview,
	} = useStagingAreaActions();
	const { forceSkipAutoConversationSelection } = useStagingAreaState();
	const onNewConversationStartingWithSendMessage = useOnNewConversationStartedWithSendMessage();
	const { preferences } = useUserPreferences();
	// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
	// value when BE user settings are fully rolled out; BE overrides it via the capability header.
	const getStoreUserMessagesPreference = useCallback(
		() => preferences.storeUserMessages,
		[preferences.storeUserMessages],
	);

	const { activeAction } = useActiveAction();

	// Skip auto conversation selection if we're already previewing a message or an action is pending.
	// When an action is set, the chat store must start with EMPTY_CONVERSATION_ID rather than
	// restoring the previous conversation from localStorage via autoSelectConversation.
	// Matters only on initialization.
	const skipAutoConversationSelection =
		!!getMessageIdSelectedForPreview?.() ||
		!!getInvocationIdSelectedForPreview?.() ||
		forceSkipAutoConversationSelection ||
		!!activeAction;

	const onTempMessageIdReplaced = useCallback(
		(oldId: string, newId: string) => {
			// Update messageIdSelectedForPreview when the message ID changes during streaming
			if (oldId === getMessageIdSelectedForPreview?.()) {
				setMessageIdSelectedForPreview(newId);
			}
		},
		[getMessageIdSelectedForPreview, setMessageIdSelectedForPreview],
	);

	const { trackAgentEvent } = useRovoAgentActionAnalytics({});
	const { setReasoningMode } = useChatModeStoreActionsRefresh();
	const { setSmartContextLinkRecommendations } = useSpaceStoreActions();

	// Build the unified tasks container dep consumed by chat-store actions.
	// Always assembled (cheap) so the container shape is stable across renders.
	// All read accessors are sweet-state actions so the dep object identity does not depend on store
	// state — chat-store consumers won't re-render on every task mutation.
	const unifiedTasksActions = useUnifiedTasksStoreActions();
	const unifiedTasksContainerDep = useMemo(
		() => ({
			createTaskFromStream: unifiedTasksActions.createTaskFromStream,
			setTaskProgressState: unifiedTasksActions.setTaskProgressState,
			setTaskTodoSnapshot: unifiedTasksActions.setTaskTodoSnapshot,
			ackTaskCompleted: unifiedTasksActions.ackTaskCompleted,
			mergeConversationChannelTasks: unifiedTasksActions.mergeConversationChannelTasks,
			getTaskById: unifiedTasksActions.getTaskById,
			getActiveTaskForConversation: unifiedTasksActions.getActiveTaskForConversation,
		}),
		[unifiedTasksActions],
	);

	const isViewDeadAirEnabled = fg('rovo_chat_track_view_dead_air');
	const viewDeadAirBroker = useMemo(
		() => (isViewDeadAirEnabled ? createViewDeadAirBroker() : null),
		[isViewDeadAirEnabled],
	);
	const capabilities = useRovoChatCapabilities();

	return (
		<>
			<ChatStoreContainer
				isGlobal
				siteId={siteId}
				onPromptSent={onPromptSent}
				assistanceService={assistanceService}
				experienceTracker={experienceTracker}
				sendAnalyticsEvent={sendAnalyticsEvent}
				trackAIMAUEvent={trackAIMAUEvent}
				chatContextStoreActions={chatContextStoreActions}
				capabilities={capabilities}
				setChatStatus={setStatus}
				setConversationError={setConversationError}
				getMessages={getMessages}
				setMessages={setMessages}
				getIsChatMounted={getIsChatMounted}
				startReconnection={startReconnection}
				endReconnection={endReconnection}
				isReconnecting={isReconnecting}
				setRovoEnabled={setIsRovoEnabled}
				processStreamResearch={processStreamResearch}
				markIncompleteResearchesErrored={markIncompleteResearchesErrored}
				completeAllSteps={completeAllSteps}
				updateResearchMessageId={updateResearchMessageId}
				clearMessageResearch={clearMessageResearch}
				addImplicitResearchPlan={addImplicitResearchPlan}
				resetHasReceivedAnswerPart={resetHasReceivedAnswerPart}
				initChatInitiator={initChatInitiator}
				getChatInitiator={getChatInitiator}
				insertHighlightActionsEditorPlaceholder={insertHighlightActionsEditorPlaceholder}
				processMessageStreamAction={processMessageStreamAction}
				processMessageStreamComplete={processMessageStreamComplete}
				processMessageStreamError={processMessageStreamError}
				processMessageStreamActionClassification={processMessageStreamActionClassification}
				broadcastStreamStatus={broadcastStreamStatus}
				broadcastConfirmedActions={broadcastConfirmedActions}
				setStagingAreaMode={setStagingAreaMode}
				setMessageIdSelectedForPreview={setMessageIdSelectedForPreview}
				onTempMessageIdReplaced={onTempMessageIdReplaced}
				skipAutoConversationSelection={skipAutoConversationSelection}
				onNewConversationStartingWithSendMessage={onNewConversationStartingWithSendMessage}
				browserContextSanitizer={browserContextSanitizer}
				getStoreUserMessagesPreference={getStoreUserMessagesPreference}
				trackAgentEvent={trackAgentEvent}
				viewDeadAirBroker={viewDeadAirBroker}
				showFlag={showFlag}
				setReasoningMode={setReasoningMode}
				unifiedTasksActions={unifiedTasksContainerDep}
				onSpaceSourceRecommendations={setSmartContextLinkRecommendations}
			>
				<StoreErrorFlagProvider showFlag={showFlag}>
					<ViewDeadAirProvider value={viewDeadAirBroker} identity={currentConversationId}>
						<BackBehaviorOverrideProvider>{children}</BackBehaviorOverrideProvider>
					</ViewDeadAirProvider>
				</StoreErrorFlagProvider>
			</ChatStoreContainer>
		</>
	);
};

/**
 * BE-backed storeUserMessages bootstrap. Must be rendered inside the main
 * conversation providers so `useAssistanceServiceParams` can read GlobalConfig.
 *
 * The enabled setup path loads `/user-settings` through `useStoreUserMessagesPreference`.
 * MigrationRunner is the temporary one-shot localStorage -> BE migration and remains
 * gated by the legacy `ai_mate_show_store_messages` eligibility flag.
 */
const StoreUserMessagesBackendSetup = (): React.JSX.Element | null => {
	if (!fg('ai_mate_store_messages_be_driven')) {
		return null;
	}

	return <StoreUserMessagesBackendSetupEnabled />;
};

const StoreUserMessagesBackendSetupEnabled = (): React.JSX.Element | null => {
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'rovo-chat-store-user-messages',
	});

	const assistanceServiceParamsKey = JSON.stringify(assistanceServiceParams);
	const stableAssistanceServiceParams = useMemo(
		() => JSON.parse(assistanceServiceParamsKey),
		[assistanceServiceParamsKey],
	);

	const {
		localStoreUserMessages,
		storeUserMessageMigratedToBE,
		markStoreUserMessageMigratedToBE,
		updateBackendStoreUserMessages,
	} = useStoreUserMessagesPreference({
		assistanceServiceParams: stableAssistanceServiceParams,
	});

	if (!fg('ai_mate_show_store_messages')) {
		return null;
	}

	return (
		<StoreUserMessagesMigrationRunner
			isCurrentUserMigrated={storeUserMessageMigratedToBE}
			markCurrentUserAsMigrated={markStoreUserMessageMigratedToBE}
			currentUserStoreMessagesPreference={localStoreUserMessages}
			updateBackendStoreUserMessages={updateBackendStoreUserMessages}
		/>
	);
};

/**
 * Wraps RovoSettingsModalProvider with the chat-specific callback for
 * "See what Rovo knows about you". Must be rendered inside ChatStoreContainer.
 */
const ChatAwareRovoSettingsModalProvider = ({
	config,
	disableSettingsModal,
	children,
}: {
	config: ConversationAssistantConfig;
	disableSettingsModal?: boolean;
	children: React.ReactNode;
}): React.JSX.Element => {
	const { sendMessage } = useChatStoreActions();
	const {
		preferences: { storeUserMessages },
	} = useUserPreferences();

	const handleSeeWhatRovoKnows = useCallback(() => {
		sendMessage({
			conversationId: EMPTY_CONVERSATION_ID,
			prompt: encodeAdf('What does Rovo know about me?'),
			// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
			// value when BE user settings are fully rolled out; BE overrides it via the capability header.
			storeUserMessages,
		});
	}, [sendMessage, storeUserMessages]);

	return (
		<RovoSettingsModalProvider
			product={config.product}
			cloudId={config.siteId}
			orgId={config.orgId}
			onSeeWhatRovoKnows={handleSeeWhatRovoKnows}
			surface={RovoSettingsModalSurface.CHAT}
			disableSettingsModal={disableSettingsModal}
		>
			{children}
		</RovoSettingsModalProvider>
	);
};

export const ConversationAssistantConfigurer = ({
	testId,
	onCloseChat,
	config,
	uiConfig,
	agentsConfig,
	agentVersioningConfig,
	isNav4Enabled,
	sitePickerRender,
	overridingChildren,
	modeConfig,
	getActions,
	prioritizeCustomActionResults,
	numStartersToShow,
	stagingAreaRef,
	topOffset = 0,
	tags,
	initialPrompt,
	suppressInitialFocus,
}: ConversationAssistantProps & {
	isNav4Enabled?: boolean;
	stagingAreaRef?: RefObject<HTMLDivElement>;
}): JSX.Element => {
	di(
		AssistanceServiceProvider,
		ChatStoreContainer,
		SmartCardProvider,
		AnalyticsContextWithSourceAndProperties,
	);

	useMemo(() => {
		// Setting devtools in useMemo to make sure we catch the first render.
		// ReactSweetStateDefaults may be undefined when bundlers tree-shake or when the
		// module isn't available at runtime. Guard access to avoid runtime exceptions.
		try {
			if (
				// eslint-disable-next-line @atlaskit/platform/no-preconditioning
				fg('ai_mate_react_sweet_state_devtools') &&
				typeof ReactSweetStateDefaults !== 'undefined' &&
				ReactSweetStateDefaults &&
				'devtools' in ReactSweetStateDefaults
			) {
				ReactSweetStateDefaults.devtools = true;
			}
		} catch {
			// swallow errors intentionally — best-effort only
		}
	}, []);

	const ufoContext = useInteractionContext();
	const ufoLabelStack = useMemo(
		() => ufoContext?.labelStack ?? [{ name: 'rovo-conversation-assistant' }],
		[ufoContext?.labelStack],
	);
	const setUFOLabelStack = useUFOLabelStackSetter();
	useMemo(() => {
		setUFOLabelStack(ufoLabelStack);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ufoLabelStack]);

	const profileData = useProfileController();
	const { isRovoEnabled } = useRovoEntitlement();
	useUIStoreSubscription();
	const { setIsChatOnboardingMiniModalOpen } = useUIStoreActions();
	const { isChatOnboardingMiniModalOpen } = useUIStore();

	useEffect(() => {
		setRovoChatProduct(config.product);
	}, [config.product]);

	const [sharedFeaturesInstrumentationId, setSharedFeaturesInstrumentationId] = useState<
		string | undefined
	>(undefined);

	const assistanceService = useMemo(
		() =>
			new AssistanceServiceImpl({
				baseUrl: config.assistanceServiceConfig?.baseUrl,
				headers: config.assistanceServiceConfig?.headers,
				channelId: config.assistanceServiceConfig?.channelId,
				product: config.product,
				experienceId: config.experienceId ?? AI_MATE_EXPERIENCE_ID,
				minionAlias: config.assistanceServiceConfig?.minionAlias,
				labelStack: ufoLabelStack,
			}),
		[
			config.assistanceServiceConfig?.baseUrl,
			config.assistanceServiceConfig?.headers,
			config.assistanceServiceConfig?.channelId,
			config.product,
			config.experienceId,
			config.assistanceServiceConfig?.minionAlias,
			ufoLabelStack,
		],
	);

	const globalConfig: GlobalConfig = useMemo(
		() =>
			createGlobalConfig({
				...config,
				profileData,
				closeChat: () => {
					onCloseChat?.();

					if (isChatOnboardingMiniModalOpen) {
						setIsChatOnboardingMiniModalOpen(false);
					}
				},
			}),
		[
			config,
			profileData,
			onCloseChat,
			setIsChatOnboardingMiniModalOpen,
			isChatOnboardingMiniModalOpen,
		],
	);
	const { agentOverriderValue, uiConfigValue } = useConversationAssistantProviderValues({
		agentsConfig,
		uiConfig,
	});

	// When initialPrompt is provided, self-publish a chat-new PubSub event
	// to trigger the existing PubSubListener handler which creates a seeded
	// conversation and auto-sends the prompt.
	const initialPromptFiredRef = useRef(false);
	const publish = usePublish('ai-mate');
	useEffect(() => {
		if (!initialPrompt || initialPromptFiredRef.current) {
			return;
		}
		initialPromptFiredRef.current = true;

		// Use requestAnimationFrame to ensure PubSubListener has subscribed
		requestAnimationFrame(() => {
			publish({
				type: 'chat-new',
				source: 'linkedConfluenceDocRovoNudge',
				data: {
					prompt: initialPrompt,
					dialogues: [],
					mode: { useCurrentPageContext: true },
				},
			});
		});
	}, [initialPrompt, publish]);

	const rovoChatXpcContextValues = useMemo(
		() =>
			getRovoChatXpcContextValues({
				mode: modeConfig?.mode,
				isFullScreen: uiConfig?.fullScreen,
				xpcProduct: config.xpcProduct,
				xpcSubProduct: config.xpcSubProduct,
				productKey: globalConfig.productKey,
			}),
		[
			config.xpcProduct,
			config.xpcSubProduct,
			globalConfig.productKey,
			modeConfig?.mode,
			uiConfig?.fullScreen,
		],
	);

	return (
		// The order of analytics properties context and experienceTracker matters here
		// for experience tracker to send correct analytics properties, it needs to be under analyticsContext
		<AnalyticsContextWithSourceAndProperties
			source="AIMate"
			mode={modeConfig?.mode ?? ConversationAssistantUIModes.SIDEBAR}
			sharedFeaturesInstrumentationId={sharedFeaturesInstrumentationId}
			isAlwaysInSmartCreateFlow={sharedFeaturesInstrumentationId !== undefined ? true : undefined}
		>
			{/* The idea is if Chat is rendered, we assume the entitlement check has been done before, and we want to allow all our platform components to have isRovoEnabled
					Use case: For integrators that only has Rovo chat as their touchpoints, they don't need to render RovoEntitlementSetter
					*/}
			<RovoEntitlementSetter isRovoEnabled={isRovoEnabled} />
			<AIMateErrorBoundary
				experienceName={ExperienceName.LOAD_PANEL}
				rethrowErrors={config.rethrowErrors ?? false}
			>
				<GlobalConfigProvider value={globalConfig}>
					<AgentOverriderProvider value={agentOverriderValue}>
						<AgentVersioningConfigProvider value={agentVersioningConfig}>
							<UIConfigProvider value={uiConfigValue}>
								<SmartCardProvider
									bridgeProduct={rovoChatXpcContextValues.bridgeProduct}
									xpcProduct={rovoChatXpcContextValues.xpcProduct}
									xpcSubProduct={rovoChatXpcContextValues.xpcSubProduct}
								>
									<RovoChatXpcContextOverride {...rovoChatXpcContextValues}>
										<FlagsProvider>
											<AssistanceServiceProvider value={assistanceService}>
												<SideNavProvider>
													<RovoChatUFOStatePublisher
														fullscreen={uiConfig?.fullScreen}
														mode={modeConfig?.mode ?? ConversationAssistantUIModes.SIDEBAR}
														shouldPublishChatOpenOnTransitionData={!overridingChildren}
													/>
													<ConversationAssistantWrapper onPromptSent={config.onPromptSent}>
														{overridingChildren ? (
															// NOTE: `overridingChildren` consumers (e.g. CwR staging-area-modal,
															// chat-only-creation-modal) are responsible for wrapping their own
															// subtree with `RovoSettingsModalProvider` so entrypoints to the
															// settings modal work via useSideNavModal(). The provider is
															// idempotent, so defensive wrapping is safe. See ASIMO-3977.
															<>
																<AnalyticsPropertiesUpdater />
																{overridingChildren}
															</>
														) : (
															<>
																<WarmChatFetches />
																<WarmChatContextBannerLinkController
																	enableBrowserContextBanner={uiConfig?.enableBrowserContextBanner}
																	shouldShowContextBanner={
																		uiConfig?.elements?.shouldShowContextBanner
																	}
																	stagingAreaEnabled={!!uiConfig?.elements?.stagingAreaEnabled}
																/>
																<FirstChatOpenUFODataPublisher />
																<>
																	<StoreUserMessagesBackendSetup />
																	<ChatAwareRovoSettingsModalProvider
																		config={config}
																		disableSettingsModal={uiConfig?.disableSettingsModal}
																	>
																		<ExperienceStart name={ExperienceName.LOAD_PANEL} />
																		<ConversationAssistantRender
																			sitePickerRender={sitePickerRender}
																			testId={testId}
																			isNav4Enabled={isNav4Enabled}
																			modeConfig={modeConfig}
																			getActions={getActions}
																			prioritizeCustomActionResults={prioritizeCustomActionResults}
																			numStartersToShow={numStartersToShow}
																			stagingAreaRef={stagingAreaRef}
																			topOffset={topOffset}
																			isInPreviewModal={false}
																			config={config}
																			setSharedFeaturesInstrumentationId={
																				setSharedFeaturesInstrumentationId
																			}
																			tags={tags}
																			suppressInitialFocus={suppressInitialFocus}
																		/>
																	</ChatAwareRovoSettingsModalProvider>
																</>
															</>
														)}
														{(fg('aifc_create_enabled') || fg('jira_aifc_enabled')) && (
															<>
																<BridgeAPIHandlers />
																<AifcDebugger />
															</>
														)}
													</ConversationAssistantWrapper>
												</SideNavProvider>
											</AssistanceServiceProvider>
										</FlagsProvider>
									</RovoChatXpcContextOverride>
								</SmartCardProvider>
							</UIConfigProvider>
						</AgentVersioningConfigProvider>
					</AgentOverriderProvider>
				</GlobalConfigProvider>
			</AIMateErrorBoundary>
		</AnalyticsContextWithSourceAndProperties>
	);
};
